import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import { useTranslation } from "react-i18next";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import MarkdownRenderer from "./MarkdownRenderer";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: "#fff",
    borderLeft: "1px solid #e0e0e0",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 16px",
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
    minHeight: "48px",
    flexShrink: 0,
  },
  title: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#333",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    marginRight: "8px",
  },
  closeButton: {
    padding: "4px",
  },
  content: {
    flex: 1,
    overflow: "auto",
    padding: "14px 16px",
    color: "#222",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: "13.5px",
    lineHeight: 1.55,
    scrollbarWidth: "thin",
    scrollbarColor: "transparent transparent",
    "&:hover": {
      scrollbarColor: grayColor[6] + " transparent",
    },
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "transparent",
      borderRadius: "3px",
    },
    "&:hover::-webkit-scrollbar-thumb": {
      background: grayColor[6],
    },

    "& h1": {
      fontSize: "15px",
      fontWeight: 600,
      color: "#222",
      margin: "16px 0 6px 0",
      padding: 0,
      lineHeight: 1.3,
      "&:first-child": { marginTop: 0 },
    },
    "& h2": {
      fontSize: "14px",
      fontWeight: 600,
      color: "#222",
      margin: "14px 0 4px 0",
      padding: 0,
      lineHeight: 1.3,
      "&:first-child": { marginTop: 0 },
    },
    "& h3": {
      fontSize: "13.5px",
      fontWeight: 600,
      color: "#333",
      margin: "10px 0 4px 0",
      padding: 0,
      lineHeight: 1.3,
      "&:first-child": { marginTop: 0 },
    },
    "& p": {
      margin: "0 0 8px 0",
      fontSize: "13.5px",
      lineHeight: 1.55,
      color: "#222",
      "&:last-child": { marginBottom: 0 },
    },
    "& ul, & ol": {
      margin: "4px 0 8px 0",
      paddingLeft: "20px",
    },
    "& li": {
      fontSize: "13.5px",
      lineHeight: 1.5,
      color: "#222",
      marginBottom: "2px",
    },
    "& strong": {
      fontWeight: 600,
      color: "#111",
    },
    "& hr": {
      border: "none",
      borderTop: "1px solid #e0e0e0",
      margin: "12px 0",
    },

    "& table": {
      borderCollapse: "collapse",
      width: "100%",
      fontSize: "12.5px",
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      margin: "4px 0 8px 0",
      display: "block",
      overflowX: "auto",
      maxWidth: "100%",
    },
    "& th": {
      backgroundColor: "#f5f5f5",
      fontWeight: 600,
      color: "#222",
      textAlign: "left",
      padding: "6px 10px",
      borderBottom: "2px solid #ddd",
      whiteSpace: "nowrap",
      position: "sticky",
      top: 0,
      fontSize: "12px",
    },
    "& td": {
      padding: "5px 10px",
      borderBottom: "1px solid #eee",
      color: "#222",
      fontSize: "12.5px",
    },
    "& tr:hover td": {
      backgroundColor: "#f5f7fa",
    },
    "& tr:nth-child(even) td": {
      backgroundColor: "#fafafa",
    },
    "& tr:nth-child(even):hover td": {
      backgroundColor: "#f0f2f5",
    },

    "& pre": {
      margin: "6px 0 10px 0",
      backgroundColor: "#1e1e1e",
      borderRadius: "6px",
      padding: "12px 14px",
      overflow: "auto",
      "& code": {
        color: "#e0e0e0",
        fontSize: "12.5px",
        fontFamily: '"Roboto Mono", "Consolas", "Courier New", monospace',
        backgroundColor: "transparent",
        padding: 0,
        borderRadius: 0,
        lineHeight: 1.5,
        whiteSpace: "pre",
      },
    },
    "& code": {
      fontFamily: '"Roboto Mono", "Consolas", "Courier New", monospace',
      fontSize: "12px",
      backgroundColor: "#f0f0f0",
      color: "#c7254e",
      padding: "1px 5px",
      borderRadius: "3px",
    },
    "& blockquote": {
      margin: "8px 0",
      padding: "8px 14px",
      borderLeft: "3px solid #999",
      backgroundColor: "#f8f8f8",
      borderRadius: "0 4px 4px 0",
      "& p": {
        color: "#333",
        margin: "0 0 4px 0",
        "&:last-child": { marginBottom: 0 },
      },
    },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: "#aaa",
    fontSize: "14px",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    textAlign: "center",
    padding: "24px",
  },
  emptyIcon: {
    fontSize: "40px",
    marginBottom: "12px",
    opacity: 0.5,
  },
}));

export default function ArtifactPanel({ artifact, onClose }) {
  const classes = useStyles();
  const { t } = useTranslation();

  if (!artifact) {
    return (
      <div className={classes.container}>
        <div className={classes.emptyState}>
          <div className={classes.emptyIcon}>≡</div>
          <div>{t("ai_chat.evaluation.artifact_empty")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <span className={classes.title}>{artifact.title}</span>
        <IconButton
          className={classes.closeButton}
          onClick={onClose}
          size="small"
        >
          <CloseIcon style={{ fontSize: 18 }} />
        </IconButton>
      </div>
      <div className={classes.content}>
        <MarkdownRenderer content={artifact.content} />
      </div>
    </div>
  );
}

ArtifactPanel.propTypes = {
  artifact: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};
